import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "../lib/utils";
import { EditorialPage } from "../components/editorial";
import StoryPageHero from "../components/story/StoryPageHero";
import Highlighter from "../components/story/Highlighter";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { posts, formatDate, type BlogPost } from "../lib/blog";

/**
 * /blog — the LetsLoop blog index (LL-0095). Posts have no image assets, so each
 * gets a generated emoji + pastel cover (matching the HappeningsStrip / How-It-
 * Works card language) plus a derived category label. A featured (newest) post
 * leads, the rest sit in a responsive card grid.
 */

// Topic -> emoji + clean label + pastel cover. First match wins; falls back to
// a generic "Community" cover. We derive a label rather than show the raw
// clusterTag (those still encode the legacy "Happenings" SEO key).
const TOPIC_RULES: { re: RegExp; emoji: string; label: string }[] = [
  { re: /coffee|monmouth|clapham/i, emoji: "☕", label: "Coffee runs" },
  { re: /walk|hackney|marsh/i, emoji: "🚶", label: "Walks" },
  { re: /\brun\b|running/i, emoji: "🏃", label: "Runs" },
  { re: /book/i, emoji: "📚", label: "Book club" },
  { re: /dinner|pizza|food|supper|brunch/i, emoji: "🍝", label: "Food" },
  { re: /neighbour|neighbor/i, emoji: "🏡", label: "Neighbours" },
  { re: /breakup|break-up/i, emoji: "🌱", label: "Fresh starts" },
  {
    re: /new (city|town)|new in town|just moved|moving/i,
    emoji: "🧭",
    label: "New in town",
  },
  { re: /saturday|night|weekend/i, emoji: "🌙", label: "Nights out" },
  { re: /park|victoria|picnic|outdoor/i, emoji: "🌳", label: "Outdoors" },
  {
    re: /friend|people|\b30\b|lonely|social/i,
    emoji: "👋",
    label: "Making friends",
  },
];

// All six are TOKENS, never literal hexes. The cards carry a text-ink label,
// which flips to near-white in dark mode, so a fixed pastel left that label
// invisible (measured at 1.0 to 1.04 against #FCE9E1, #EAF1F0 and #FFF4D6).
const PALETTE = [
  "var(--color-paper-salmon)",
  "var(--color-paper-mint)",
  "var(--color-paper-tan)",
  "var(--color-paper-blush)",
  "var(--color-paper-sky)",
  "var(--color-paper-sand)",
];

function coverFor(post: BlogPost, index: number) {
  const hay = `${post.title} ${post.slug} ${post.clusterTag ?? ""}`;
  const rule = TOPIC_RULES.find((r) => r.re.test(hay));
  return {
    emoji: rule?.emoji ?? "✨",
    label: rule?.label ?? "Community",
    bg: PALETTE[index % PALETTE.length],
  };
}

export default function BlogIndexPage() {
  useEffect(() => {
    document.title = "News: meeting people, locally | LetsLoop";
  }, []);

  const [featured, ...rest] = posts;

  return (
    <>
      <Navbar />
      <EditorialPage>
        <StoryPageHero
          kicker="news"
          title={
            <>
              Meeting people, <Highlighter tone="mint">locally.</Highlighter>
            </>
          }
          lede={
            <>
              Practical, calm guides to building a real social life wherever you
              are. New plans, recurring communities, and the small things that
              turn strangers into familiar faces.
            </>
          }
          marks={[
            {
              name: "squiggle",
              size: 76,
              rotate: -5,
              className: "right-[7%] top-[26%] text-teal/35",
            },
            {
              name: "starFilled",
              size: 22,
              className: "left-[5%] top-[24%] text-salmon/60",
            },
            {
              name: "heart",
              size: 32,
              rotate: 12,
              className: "right-[22%] bottom-[14%] text-salmon-dark/45",
            },
          ]}
        />

        <div className="relative max-w-5xl mx-auto px-4 md:px-6 pt-4 pb-20 md:pb-28">
          {posts.length === 0 ? (
            <p className="text-gray-600 leading-relaxed">
              New writing is on the way. Check back soon.
            </p>
          ) : (
            <>
              {/* Featured (newest) post */}
              {featured &&
                (() => {
                  const cover = coverFor(featured, 0);
                  return (
                    <Link
                      to={`/news/${featured.slug}`}
                      className="group block mb-12 md:mb-16"
                    >
                      <div className="flex flex-col md:flex-row overflow-hidden rounded-3xl border border-ink/15 bg-canvas shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] transition-shadow duration-300 hover:shadow-[0_22px_50px_-16px_rgba(5,5,5,0.26)]">
                        <div
                          className="relative flex items-center justify-center overflow-hidden md:w-[44%] min-h-[220px] md:min-h-[340px]"
                          style={{ backgroundColor: cover.bg }}
                        >
                          {/* A real photo when the post has one; the generated
                              emoji cover is the fallback, not the default. */}
                          {featured.image ? (
                            <img
                              src={featured.image}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              loading="eager"
                            />
                          ) : (
                            <span className="text-7xl md:text-8xl transition-transform duration-300 group-hover:scale-110">
                              {cover.emoji}
                            </span>
                          )}
                          <span
                            className={cn(
                              "absolute top-5 left-5 text-[11px] font-bold uppercase tracking-[0.2em]",
                              featured.image
                                ? "rounded-full bg-[#141210]/65 px-3 py-1.5 text-[#f9f9f7] backdrop-blur-sm"
                                : "text-ink/55",
                            )}
                          >
                            Latest
                          </span>
                        </div>
                        <div className="flex flex-col justify-center p-7 md:p-12 md:w-[56%]">
                          <div className="flex items-center gap-3 mb-3 text-xs font-semibold uppercase tracking-widest text-ink/45">
                            <span className="text-salmon">{cover.label}</span>
                            {featured.date && (
                              <>
                                <span className="h-1 w-1 rounded-full bg-ink/25" />
                                <span>
                                  {formatDate(featured.date)} ·{" "}
                                  {featured.readingMinutes} min
                                </span>
                              </>
                            )}
                          </div>
                          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.05] text-ink group-hover:text-salmon transition-colors">
                            {featured.title}
                          </h2>
                          {featured.description && (
                            <p className="text-gray-600 leading-relaxed mt-4 text-base md:text-lg line-clamp-3">
                              {featured.description}
                            </p>
                          )}
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-salmon mt-6">
                            Read the latest
                            <ArrowRight
                              size={16}
                              className="group-hover:translate-x-0.5 transition-transform"
                            />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })()}

              {/* Card grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                  {rest.map((post, i) => {
                    const cover = coverFor(post, i + 1);
                    return (
                      <Link
                        key={post.slug}
                        to={`/news/${post.slug}`}
                        className="group flex flex-col overflow-hidden rounded-3xl border border-ink/15 bg-canvas shadow-[0_14px_36px_-14px_rgba(5,5,5,0.14)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_50px_-16px_rgba(5,5,5,0.22)]"
                      >
                        <div
                          className="relative flex items-center justify-center overflow-hidden h-40"
                          style={{ backgroundColor: cover.bg }}
                        >
                          {post.image ? (
                            <img
                              src={post.image}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-6xl transition-transform duration-300 group-hover:scale-110">
                              {cover.emoji}
                            </span>
                          )}
                          {/* Over a photograph the label needs its own ground:
                              a tint token cannot be relied on behind an image. */}
                          <span
                            className={cn(
                              "absolute top-4 left-4 text-[11px] font-bold uppercase tracking-[0.18em]",
                              post.image
                                ? "rounded-full bg-[#141210]/65 px-3 py-1.5 text-[#f9f9f7] backdrop-blur-sm"
                                : "text-ink/55",
                            )}
                          >
                            {cover.label}
                          </span>
                        </div>
                        <div className="flex flex-1 flex-col p-6">
                          {post.date && (
                            <p className="text-xs font-semibold uppercase tracking-widest text-ink/40 mb-2">
                              {formatDate(post.date)} · {post.readingMinutes}{" "}
                              min
                            </p>
                          )}
                          <h3 className="font-serif text-xl md:text-2xl tracking-tight leading-snug text-ink group-hover:text-salmon transition-colors">
                            {post.title}
                          </h3>
                          {post.description && (
                            <p className="text-gray-600 leading-relaxed mt-3 text-sm md:text-base line-clamp-3">
                              {post.description}
                            </p>
                          )}
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-salmon mt-auto pt-5">
                            Read
                            <ArrowRight
                              size={15}
                              className="group-hover:translate-x-0.5 transition-transform"
                            />
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </EditorialPage>
      <Footer />
    </>
  );
}
